import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import useEventListener from '@/hooks/event-listener';
import { tools } from '@/utils/tools';

export default function useVirtualCardList(props, emit) {
  const { eventListener } = useEventListener();

  // 滚动节流时间（毫秒）
  const scrollThrottleMs = 16;

  const containerRef = ref(null);
  const itemRefs = new Map(); // 存储DOM引用

  // 状态
  const scrollTop = ref(0);
  const itemHeights = ref([]); // 存储每个item的实际高度
  // 所有数据项
  const allItems = computed(() => props.records || []);
  const defaultItemHeight = 260;

  // 预计算累积高度的缓存
  const cumulativeHeights = ref([]);
  const lastMeasuredIndex = ref(-1);

  // 标志位，用于控制是否需要重新计算布局
  const needRecalculate = ref(false);

  // 计算属性
  const containerHeight = computed(() => props.height);

  // 计算从开始到结束的总高度
  const totalHeight = computed(() => {
    updateCumulativeHeights();
    return cumulativeHeights.value[allItems.value.length] || allItems.value.length * defaultItemHeight;
  });

  // 计算需要渲染的起始索引
  const startIndex = computed(() => {
    if (allItems.value.length === 0) return 0;

    // 使用二分查找快速定位可见区域的起始位置
    const index = findIndexOffset(scrollTop.value);

    // 添加缓冲区，提前渲染一些项目
    const bufferStart = Math.max(0, index - props.bufferCount);
    return bufferStart;
  });

  // 计算需要渲染的结束索引
  const endIndex = computed(() => {
    if (allItems.value.length === 0) return 0;

    // 计算可见区域的结束位置
    const visibleEnd = scrollTop.value + containerHeight.value;
    let endIdx = findIndexOffset(visibleEnd);

    // 添加缓冲区，多渲染一些项目
    endIdx = Math.min(allItems.value.length - 1, endIdx + props.bufferCount);

    return endIdx;
  });

  // 计算可见的数据项
  const visibleItems = computed(() => {
    return allItems.value.slice(startIndex.value, endIndex.value + 1);
  });

  // 计算内容的垂直偏移量，用于定位可见内容
  const offsetY = computed(() => {
    // 获取起始索引的累积高度
    return getOffsetHeight(startIndex.value);
  });

  // 初始化数据
  const initData = () => {
    // 初始化高度缓存
    const len = allItems.value.length;
    itemHeights.value = [];
    for (let i = 0; i < len; i++) {
      itemHeights.value.push(defaultItemHeight); // 逐个添加默认值
    }
  };

  // 更新累积高度缓存
  const updateCumulativeHeights = (upToIndex = allItems.value.length - 1) => {
    // 性能优化：跳过不必要的计算
    if (upToIndex <= lastMeasuredIndex.value) return;

    // 从上次测量的索引开始计算
    const startIdx = Math.max(0, lastMeasuredIndex.value);
    const newCumulativeHeights = [...cumulativeHeights.value];

    if (startIdx === 0) {
      newCumulativeHeights[0] = 0;
    }

    for (let i = startIdx; i <= upToIndex; i++) {
      newCumulativeHeights[i + 1] = (newCumulativeHeights[i] || 0) + getItemHeight(i);
    }

    cumulativeHeights.value = newCumulativeHeights;
    lastMeasuredIndex.value = upToIndex;
  };

  // 使用二分查找查找给定偏移量对应的索引
  const findIndexOffset = (offset) => {
    updateCumulativeHeights();

    let low = 0;
    let high = allItems.value.length;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const midOffset = cumulativeHeights.value[mid] || 0;

      if (midOffset <= offset && (mid === allItems.value.length || cumulativeHeights.value[mid + 1] > offset)) {
        return mid;
      } else if (midOffset < offset) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    return 0;
  };

  // 设置元素引用
  const setItemRef = (index, el) => {
    if (el) {
      itemRefs.set(index, el);
      // 初始测量高度
      nextTick(() => {
        updateItemHeight(index, el.offsetHeight);
      });
    } else {
      itemRefs.delete(index);
    }
  };

  // 更新项目高度   index 是ref 索引
  const updateItemHeight = (index, height) => {
    if (itemHeights.value[index] !== height) {
      const oldHeight = itemHeights.value[index];
      itemHeights.value[index] = height;
      // 清除受影响的累积高度缓存，确保后续计算准确
      // 如果当前索引小于等于上次测量索引，需要重新计算从该索引开始的累积高度
      if (index <= lastMeasuredIndex.value) {
        // 将lastMeasuredIndex设置为小于当前索引的值，这样下次调用updateCumulativeHeights时会重新计算
        lastMeasuredIndex.value = Math.min(index - 1, -1);
      }
      // 触发高度变化事件
      emit('itemHeightChange', { index, oldHeight, newHeight: height });
      // 标记需要重新计算布局
      needRecalculate.value = true;
    }
  };

  // 重新计算整个布局
  const recalculateLayout = () => {
    if (!needRecalculate.value) return;

    // 重置累积高度缓存
    cumulativeHeights.value = [];
    lastMeasuredIndex.value = -1;

    // 更新所有可见项的高度
    updateVisibleItemsHeight();

    // 重置标志位
    needRecalculate.value = false;
  };

  // 监听窗口大小变化，重新计算布局
  const handleResize = () => {
    needRecalculate.value = true;

    // 使用requestAnimationFrame优化性能
    requestAnimationFrame(() => {
      recalculateLayout();
    });
  };

  // 使用工具类的防抖函数处理窗口大小变化
  const debouncedHandleResize = tools.debounceFn(handleResize, 200, false);

  // 优化的可见项更新逻辑
  const updateVisibleItemsHeight = () => {
    // 更新所有可见项
    for (let i = startIndex.value; i < endIndex.value; i++) {
      const el = itemRefs.get(i);
      if (el) {
        updateItemHeight(i, el.offsetHeight);
      }
    }
  };

  // 处理滚动事件中的更新可见项高度（用于防抖）
  const updateVisibleItemsOnScroll = () => {
    nextTick(() => {
      updateVisibleItemsHeight();
    });
  };

  // 使用工具类的防抖函数处理滚动事件中的可见项更新
  const debouncedUpdateVisibleItems = tools.debounceFn(updateVisibleItemsOnScroll, scrollThrottleMs, false);

  // 处理滚动事件
  const handleScroll = (event) => {
    const _scrollTop = event.target.scrollTop;
    scrollTop.value = _scrollTop;

    // 滚动条触底检测
    const container = event.target;
    const { scrollHeight, clientHeight } = container;
    const isBottom = _scrollTop + clientHeight >= scrollHeight - 10; // 10px 容差

    if (isBottom) {
      emit('scrollBottom');
    }

    // 调用防抖处理的可见项更新
    debouncedUpdateVisibleItems();
  };

  // 获取项目的高度
  const getItemHeight = (index) => {
    return itemHeights.value[index] !== undefined ? itemHeights.value[index] : defaultItemHeight;
  };

  // 获取元素到顶部的偏移量
  const getOffsetHeight = (index) => {
    if (index <= 0) return 0;

    // 使用累积高度缓存进行快速计算
    updateCumulativeHeights(index - 1);

    return cumulativeHeights.value[index] || 0;
  };

  // 生命周期钩子
  onMounted(() => {
    initData();
    nextTick(() => {
      updateVisibleItemsHeight();
    });

    // 添加窗口大小变化监听
    eventListener('resize', debouncedHandleResize);

    // 定期检查是否需要重新计算布局
    // 使用requestAnimationFrame循环而非事件监听
    let animationFrameId = null;
    const scheduleRecalculate = () => {
      if (needRecalculate.value) {
        recalculateLayout();
      }
      animationFrameId = requestAnimationFrame(scheduleRecalculate);
    };
    animationFrameId = requestAnimationFrame(scheduleRecalculate);
    // 组件卸载时清理
    onBeforeUnmount(() => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    });
  });

  onBeforeUnmount(() => {
    itemRefs.clear();
  });

  // 返回需要暴露的状态和方法
  return {
    containerRef,
    scrollTop,
    totalHeight,
    startIndex,
    endIndex,
    visibleItems,
    offsetY,
    setItemRef,
    handleScroll,
    updateItemHeight,
    recalculateLayout
  };
}
